import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
  Rating as StarRating,
  Stack,
  Box,
} from '@mui/material';

import OrderWorkunit from 'types/entities/OrderWorkunit';
import { MessageType } from 'types/enums/MesageType';

import React, { useCallback, useEffect, useState } from 'react';
import NotificationApiService from 'api/NotificationApiService';
import { MissionAdvancementPossibilities } from 'types/enums/MissionAdvancementPossibilities';
import useDeliverableService from 'features/Deliverables/hooks/useDeliverableService';
import { useAppState } from 'store/Provider';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import { closeDialogAction } from 'store/actions/dialogsActions';

const DeliverableValidateRatingsDialog: React.FC = () => {
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const open = useSelector((state) => state.dialogs.deliverableAdvancementRatingDialog);
  const appState = useSelector((state) => state.app);

  const dialogState = useSelector((state) => state.dialogs);

  const {
    quickActionfn = undefined,
    activeDeliverable = undefined,
    acceptState = undefined,
    accept = undefined,
    refuse = undefined,
    withReserve = undefined,
    reservationButton = undefined,
  } = dialogState?.data ?? {
    quickActionfn: undefined,
    activeDeliverable: undefined,
    acceptState: undefined,
    accept: undefined,
    refuse: undefined,
    withReserve: undefined,
    reservationButton: false,
  };
  const [comment, setComment] = useState('');
  const [withReserveChecked, setWithReserveChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  // RAting related
  const [skipAssessmentChecked, setSkipAssessmentChecked] = useState(false);
  const [value, setValue] = React.useState(0);
  const starLabels: { [index: string]: string } = {
    5: '5',
    4: '4',
    3: '3',
    2: '2',
    1: '1',
    0: '0',
  };

  const handleCancel = () => {
    setComment('');
    setValue(0);
    setSkipAssessmentChecked(false);
    setWithReserveChecked(false);
    setLoading(false);
    dispatch(closeDialogAction('deliverableAdvancementRatingDialog'));
  };

  React.useEffect(() => {
    if (skipAssessmentChecked) {
      setValue(0);
    }
  }, [skipAssessmentChecked]);

  const {
    changeMissionAdvancement,
    changeMissionAdvancementRating,
    setRatingDeliverables,
    addCommentDeliverableRating,
  } = useDeliverableService();

  const validateDeliverable = async (missionToChange: OrderWorkunit, validation: number) => {
    const rating: string = starLabels[value] ?? 'S';
    await changeMissionAdvancementRating(
      activeDeliverable,
      validation,
      true,
      false,
      skipAssessmentChecked ? 'S' : rating,
      quickActionfn
    );

    // await changeMissionAdvancement([missionToChange.id], validation);

    if (validation === MissionAdvancementPossibilities.REFUSED_CLIENT) {
      await setRatingDeliverables(
        [{ ...missionToChange, mission_advancement_id: MissionAdvancementPossibilities.REFUSED_CLIENT }],
        '0',
        null
      );
    }
  };

  const sendNewComment = async (mission_advancement_id: number) => {
    const commentAdded = await addCommentDeliverableRating(
      activeDeliverable as OrderWorkunit,
      {
        comment,
        mission_advancement: mission_advancement_id,
        is_private: !(mission_advancement_id > MissionAdvancementPossibilities.ACCEPTATION_DC),
        rating: starLabels[value] ?? 'S',
      },
      quickActionfn
    );
    if (commentAdded && quickActionfn === 'single') {
      await NotificationApiService.Instance.postNotification({
        type: MessageType.mission_just_confirmed,
        appendix: commentAdded.id,
        uri: `/${appState.customer?.slug}/deliverables/${activeDeliverable?.order_id}`,
        wording: (activeDeliverable?.workunit_reference as string) ?? (activeDeliverable?.workunit_name as string),
      });
    }

    return commentAdded;
  };
  const handleDialogCallbackCall = async () => {
    setLoading(true);
    if (acceptState && !withReserveChecked) {
      if (comment !== '') await sendNewComment(accept);
      if (typeof comment !== 'undefined') await validateDeliverable(activeDeliverable as OrderWorkunit, accept);
      else await validateDeliverable(activeDeliverable as OrderWorkunit, accept);
    } else if (withReserveChecked && acceptState) {
      await sendNewComment(withReserve[0]);

      await validateDeliverable(activeDeliverable as OrderWorkunit, withReserve[0]);
    } else if (!acceptState) {
      await sendNewComment(refuse);
      await validateDeliverable(activeDeliverable as OrderWorkunit, refuse);
    }
    handleCancel();
  };

  const displayAction = useCallback(() => {
    const submitText = acceptState ? t('Submit_accepted') : t('Submit_refusal');
    let tooltip = null;

    if (comment === '') {
      if ((acceptState && withReserveChecked) || !acceptState) {
        tooltip = (
          <Tooltip
            title={t<string>('Comment required')}
            componentsProps={{
              popper: {
                className: 'tooltip-dialog',
              },
            }}
          >
            <span>{submitText}</span>
          </Tooltip>
        );
      }
    }

    return tooltip || submitText;
  }, [acceptState, comment, t, withReserveChecked]);

  function getStarLabelText(value: number) {
    return `${value} Star${value !== 1 ? 's' : ''}, ${starLabels[value]}`;
  }

  return (
    <Dialog open={open} fullWidth id="dialog-confirm-validate">
      <DialogTitle
        sx={{
          pb: 0,
          borderBottom: 'none', // Explicitly remove any bottom border
          boxShadow: 'none', // Ensure no shadow is acting like a line
        }}
      >
        {acceptState ? t('Validate_tm') : t('refuse')}
      </DialogTitle>

      <DialogContent sx={{ pt: -2 }}>
        <Stack spacing={0}>
          {acceptState && (
            <>
              <Box sx={{ ml: -0.5 }}>
                <StarRating
                  name="hover-feedback"
                  value={value}
                  precision={1}
                  sx={{
                    marginTop: -1,
                    fontSize: '3.5rem',
                    '& .MuiRating-icon': {
                      marginRight: '4px',
                    },
                  }}
                  disabled={skipAssessmentChecked}
                  getLabelText={getStarLabelText}
                  onChange={(event, newValue) => {
                    const rating = newValue ?? 0;
                    setValue(rating);
                  }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small" // Makes the checkbox icon smaller
                    name={t('skip_assessment')}
                    checked={skipAssessmentChecked}
                    onChange={(e) => setSkipAssessmentChecked(e.target.checked)}
                    sx={{ py: 0.5 }} // Reduces vertical padding around the checkbox
                  />
                }
                label={<Typography sx={{ fontSize: '0.8125rem' }}>{t('skip_assessment')}</Typography>}
                sx={{ marginY: 0 }} // Removes default vertical margins
              />
              {reservationButton && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      name={t('with_reserve')}
                      checked={withReserveChecked} // Changed 'value' to 'checked'
                      onChange={(e) => setWithReserveChecked(e.target.checked)}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.8125rem' }}>{t('Accept_with_reserve')}</Typography>}
                  sx={{ marginY: 0 }}
                />
              )}
            </>
          )}
          <Box sx={{ mt: acceptState ? 2 : 0 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t('Comment')}
              {((acceptState && withReserveChecked) || !acceptState || (value < 4 && !skipAssessmentChecked)) && (
                <sup>
                  <Typography color="error" display="inline">
                    *{' '}
                  </Typography>
                </sup>
              )}
            </Typography>
            <TextField
              multiline
              fullWidth
              rows={4}
              placeholder="Please share your comments..."
              value={comment || ''}
              onChange={(e) => setComment(e.currentTarget.value)}
              sx={{ mb: 2, fontSize: 10 }}
              inputProps={{
                maxLength: 250, // Set the maximum number of characters here
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mt: -1 }}>
        <LoadingButton disabled={loading} loading={loading} autoFocus onClick={handleCancel}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={
            loading ||
            // CASE 1: REFUSED (acceptState is false) -> Comment is mandatory
            (!acceptState && !comment.trim()) ||
            // CASE 2: ACCEPTED (acceptState is true)
            (acceptState &&
              // Rule A: Selection is mandatory (Must have stars OR skip checked)
              ((!skipAssessmentChecked && value === 0) ||
                // Rule B: If "With Reserve" is checked -> Comment is mandatory
                (withReserveChecked && !comment.trim()) ||
                // Rule C: If < 4 stars AND not skipping -> Comment is mandatory
                (value > 0 && value < 4 && !skipAssessmentChecked && !comment.trim())))
          }
          onClick={() => handleDialogCallbackCall()}
          color={acceptState ? 'success' : 'error'}
        >
          {displayAction()}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeliverableValidateRatingsDialog;
